<!--
  THE ROW THE SEARCH PUTS AT THE TOP OF THE RAIL (#1182 ROUND 2c). Johanna's frames 1393:426222 and
  1442:246664 draw `All results` at the rail's ROOT - directly under the search box, ABOVE the
  `My Workspace` heading, inside no section - so it is emitted here, before both arms, rather than
  inside either of them. Until this round it lived in `items`, which this template renders at exactly
  one place (the private arm), so inside a team the row was ABSENT rather than unselected.

  It goes through `#navigationItem` like every other selectable row, which is what keeps the rail on
  ONE selection rule: the shared comparison, `data-testid` and `[attr.data-filter]` all come with it.
  `searchResultsTeam` is the tenant the search ran under, so the rule's tenant half is true by
  construction and clicking the row cannot move the tenant.
-->
@if (identityResolved() && advancedSearchApplied) {
  <ng-container
    [ngTemplateOutlet]="navigationItem"
    [ngTemplateOutletContext]="{ item: searchResultsItem, index: 0, depth: 0, team: searchResultsTeam }"
  />
}

@if (!identityResolved()) {
  <!--
    THE `Loading workspaces…` TEXT LINE IS GONE (#1180 ROW 3). It was 12 px of grey text above ~700 px
    of empty rail, and it is replaced by blocks in the shape of the things that are coming. That is a
    user-visible change to shipped behaviour which Johanna's issue never asks for; it is made because
    the skeleton IS the answer to "is the menu coming or broken", and two shipped specs that asserted
    the string are rewritten to assert the blocks instead.
    NOTHING IS DRAWN UNTIL THE THRESHOLD. Before it, this arm renders empty - which is what keeps a
    normal connection from ever flashing a block, and is why there is no `@else` here.
  -->
  @if (railSkeleton()) {
    <div class="rail-skeleton-menu" aria-busy="true">
      <!--
        The three section headers the frame draws, at its three drawn widths. `aria-busy` sits HERE,
        on the pending region, and never on the rail host: marking the whole rail busy while one part
        of it loads is a bigger claim than the truth.
      -->
      <app-rail-skeleton variant="section-header" [barWidth]="140" />

      <!--
        MY WORKSPACE IS OPEN BY DEFAULT TOO (`myWorkspaceExpanded`), so its rows are part of what a
        person is waiting for, exactly as the team's are (Decided 1). Drawing its header and none of its
        contents was the one shape that is wrong whichever way identity lands: the top third of the menu
        stood blank and then shoved the whole rail down as `primaryItems` and `View more` arrived, or
        the header vanished having promised a section that never came (#1194 review F1).

        The count is `primaryItems` itself - the array the loaded section renders - never a number typed
        here, so the two cannot drift apart.
      -->
      <div class="rail-section-content">
        @for (item of primaryItems; track item.id) {
          <app-sidebar-item class="rail-navigation-item" [skeleton]="true" />
        }

        <app-rail-skeleton class="rail-skeleton-view-more" variant="view-more" [barWidth]="90" />
        @if (teamMoreExpanded()) {
          @for (row of teamMoreSkeletonRows; track row) {
            <app-sidebar-item class="rail-navigation-item" [skeleton]="true" />
          }
        }
      </div>

      <div class="rail-divider"></div>

      <app-rail-skeleton variant="section-header" [barWidth]="90" />

      <div class="rail-divider"></div>

      <app-rail-skeleton variant="section-header" [barWidth]="120" />

      <!--
        Team Workspace is expanded by default, so its rows are part of what a person is waiting for
        (Decided 1). The rows come from the shared ROW component rather than a second implementation:
        one `skeleton` input, and the row's own padding and icon column place the blocks.
      -->
      <div class="rail-section-content">
        <app-rail-skeleton variant="team-row" [barWidth]="120" />

        <!--
          `teamSkeletonRows`, NOT `teamPrimaryItems`: the loaded section runs that same array but emits
          `Assigned to me` inside the loop after index 0, so three rows land where the array has two
          (#1037's notes / Team count). The skeleton drew two, and one row plus a `View more` appeared
          out of nowhere below blocks that had not moved (#1194 review F2).
        -->
        @for (row of teamSkeletonRows; track row) {
          <app-sidebar-item class="rail-navigation-item" [skeleton]="true" />
        }

        <app-rail-skeleton class="rail-skeleton-view-more" variant="view-more" [barWidth]="90" />
      </div>
    </div>
  }
} @else if (!currentTeamWorkspace) {
  <!--
    `baseItems`, not `items`: since #1182 round 2c the All results row is drawn once at the rail's
    root for both arms, and `items` still carries it for the five other apps' rails, which render
    that array unchanged. `index` addresses `elements` - the rail's `#elRef` children in DOM order,
    which the base rail uses to anchor the drop popover for a FOREIGN type - so this list starts one
    further down whenever the root row is drawn. That shift is `rootRowOffset`, named once on the
    shared base rail and added HERE and in the team arm's two outlets below: this round first wrote it
    inline in this one arm and left the other pointing a row up (#1212 review F1).
  -->
  <app-sidebar-item
    class="rounded [&:not(.drag-over)]:hover:bg-hover"
    *nestedFor="
      let item of baseItems;
      let index = index;
      let depth = depth;
      let expandable = expandable;
      let expanded = expanded;
      stateKey: StateKey.notesSidebarNotebooksTree + team?.id
    "
    [title]="item.title"
    [icon]="item.icon"
    [depth]="depth + (team ? 2 : 0)"
    [expandable]="expandable"
    [expanded]="expanded.value"
    [active]="currentTeamId === team?.id && sidebarFilter === item.id"
    (onExpand)="expanded.next($event)"
    (appClick)="selectFilter(item.id)"
    [appSidebarContextMenu]="item.id"
    #elRef
    appDroppable
    appDroppableHoverClass="drag-over"
    [appDroppablePredicate]="dndPredicate"
    (appDroppableDrop)="dndDrop($event, item.id, index + rootRowOffset, team, item.title)"
  />

  <app-sidebar-items-divider />

  <app-side-bar-notebooks (selectFilter)="selectFilter($event)" [team]="team" />
} @else {
  @if (currentMemberMailbox) {
    <section class="rail-section">
      <button
        type="button"
        class="rail-section-heading"
        [attr.aria-expanded]="myWorkspaceExpanded()"
        (click)="myWorkspaceExpanded.set(!myWorkspaceExpanded())"
      >
        <app-icon name="sapphire-chevron-right" size="s" [class.rotate-90]="myWorkspaceExpanded()" />
        My Workspace
      </button>

      @if (myWorkspaceExpanded()) {
        <div class="rail-section-content">
          @for (item of primaryItems; track item.id; let index = $index) {
            <ng-container
              [ngTemplateOutlet]="navigationItem"
              [ngTemplateOutletContext]="{ item: item, index: index + rootRowOffset, team: currentMemberMailbox }"
            />
          }

          <button
            type="button"
            class="rail-view-more"
            [attr.aria-expanded]="moreExpanded()"
            (click)="moreExpanded.set(!moreExpanded())"
          >
            <app-icon name="sapphire-chevron-right" size="s" [class.rotate-90]="moreExpanded()" />
            View more
          </button>

          @if (moreExpanded()) {
            @for (item of moreItems; track item.id; let index = $index) {
              <ng-container
                [ngTemplateOutlet]="navigationItem"
                [ngTemplateOutletContext]="{
                  item: item,
                  index: index + primaryItems.length + rootRowOffset,
                  team: currentMemberMailbox,
                }"
              />
              @if (item.id === 'snoozed') {
                <app-sidebar-item
                  data-testid="rail-row"
                  data-filter="following"
                  data-tenant="member"
                  [title]="'Following'"
                  icon="bell"
                  [comingSoon]="true"
                  [depth]="0"
                  appSidebarContextMenu="following"
                  [appSidebarContextMenuTenantId]="currentMemberMailbox.id"
                  [appSidebarContextMenuRail]="true"
                  appSidebarContextMenuRailScope="mine"
                />
              }
            }
          }

          <!--
            `[tenantId]` is what makes this tree the MEMBER's notebooks while the team's tree below is
            the space's — both are on screen at once, so the stored selection cannot answer for both.
          -->
          <div class="rail-item-divider"></div>

          <app-side-bar-notebooks
            [depth]="0"
            [rail]="true"
            railScope="mine"
            [tenantId]="currentMemberMailbox.id"
            (selectFilter)="selectFilter($event, currentMemberMailbox)"
            [team]="team"
          />

          <div class="rail-item-divider"></div>

          <app-knowledge-items-rail
            [sidebarSplitViewKey]="sidebarSplitViewKey"
            [tenantId]="currentMemberMailbox.id"
            [team]="currentMemberMailbox"
            [knotsRailEntries]="pinnedMenus.mine.knots"
            [tagsRailEntries]="pinnedMenus.mine.tags"
            knotsManageLabel="Manage Pinned Knots"
            tagsManageLabel="Manage Pinned Tags"
          />
        </div>
      }
    </section>

    <div class="rail-divider"></div>
  }

  <app-sidebar-shared-placeholder
    [rows]="sharedItems"
    [headerTemplate]="currentMemberMailbox ? notesSharedHeader : undefined"
  />

  <div class="rail-divider"></div>

  <section class="rail-section">
    <button
      type="button"
      class="rail-section-heading"
      [attr.aria-expanded]="teamWorkspaceExpanded()"
      (click)="teamWorkspaceExpanded.set(!teamWorkspaceExpanded())"
    >
      <app-icon name="sapphire-chevron-right" size="s" [class.rotate-90]="teamWorkspaceExpanded()" />
      Team Workspace
    </button>

    @if (teamWorkspaceExpanded()) {
      <div class="rail-section-content">
        <button
          type="button"
          class="rail-workspace-row"
          [attr.aria-expanded]="currentTeamWorkspaceExpanded()"
          (click)="toggleCurrentTeamWorkspace()"
        >
          <app-icon name="sapphire-chevron-right" size="s" [class.rotate-90]="currentTeamWorkspaceExpanded()" />
          <span
            class="rail-workspace-avatar"
            [style.background-color]="workspaceColor(currentTeamWorkspace)"
            [style.color]="currentTeamWorkspace.color ? null : workspacePalette(currentTeamWorkspace).text"
          >
            {{ currentTeamWorkspaceInitials }}
          </span>
          <span class="rail-workspace-title">{{ currentTeamWorkspace.title }}</span>
        </button>

        @if (currentTeamWorkspaceExpanded()) {
          <div class="rail-team-workspace-content">
            @for (item of teamPrimaryItems; track item.id; let index = $index) {
              <ng-container
                [ngTemplateOutlet]="navigationItem"
                [ngTemplateOutletContext]="{ item: item, index: index, depth: 0, team: currentTeamMailbox }"
              />

              @if (index === 0) {
                <app-sidebar-item
                  data-testid="rail-row-assigned-to-me"
                  data-filter="assigned_to_me"
                  data-tenant="team"
                  class="rail-navigation-item rounded"
                  [title]="'Assigned to me'"
                  icon="sapphire-user-plus"
                  [depth]="0"
                  [active]="
                    (currentTeamId ?? null) === (currentTeamMailbox?.id ?? null) && sidebarFilter === 'assigned_to_me'
                  "
                  [badge]="assignmentFeed?.unreadCount() ? { template: assignedBadge, context: {} } : undefined"
                  (appClick)="selectFilter('assigned_to_me', currentTeamMailbox)"
                  appSidebarContextMenu="assigned_to_me"
                  [appSidebarContextMenuTenantId]="currentTeamMailbox?.id"
                  [appSidebarContextMenuRail]="true"
                  appSidebarContextMenuRailScope="team"
                />
              }
            }

            <button
              type="button"
              class="rail-view-more rail-team-view-more"
              [attr.aria-expanded]="teamMoreExpanded()"
              (click)="teamMoreExpanded.set(!teamMoreExpanded())"
            >
              <app-icon name="sapphire-chevron-right" size="s" [class.rotate-90]="teamMoreExpanded()" />
              View more
            </button>

            @if (teamMoreExpanded()) {
              @for (item of moreItems; track item.id; let index = $index) {
                <ng-container
                  [ngTemplateOutlet]="navigationItem"
                  [ngTemplateOutletContext]="{ item: item, index: index, depth: 0, team: currentTeamMailbox }"
                />
                @if (item.id === 'snoozed') {
                  <app-sidebar-item
                    data-testid="rail-row"
                    data-filter="following"
                    data-tenant="team"
                    [title]="'Following'" icon="bell" [depth]="0"
                    [active]="currentTeamId === currentTeamMailbox?.id && sidebarFilter === 'following'"
                    (appClick)="selectFilter('following', currentTeamMailbox)"
                    [appSidebarContextMenu]="'following'"
                    [appSidebarContextMenuTenantId]="currentTeamMailbox?.id"
                    [appSidebarContextMenuRail]="true"
                    appSidebarContextMenuRailScope="team"
                  />
                }
              }
            }
          </div>
        }

        <div class="rail-item-divider"></div>

        <app-side-bar-notebooks
          title="Team Notebooks"
          [depth]="0"
          [rail]="true"
          railScope="team"
          [tenantId]="currentTeamWorkspace.id"
          (selectFilter)="selectFilter($event, currentTeamMailbox)"
          [team]="currentTeamMailbox"
        />

        <div class="rail-item-divider"></div>

        <app-knowledge-items-rail
          knotsTitle="Team Knots"
          tagsTitle="Team Tags"
          [sidebarSplitViewKey]="sidebarSplitViewKey"
          [tenantId]="currentTeamWorkspace.id"
          [team]="currentTeamMailbox"
          [depth]="0"
          [knotsRailEntries]="pinnedMenus.team.knots"
          [tagsRailEntries]="pinnedMenus.team.tags"
          knotsManageLabel="Manage Team Knots"
          tagsManageLabel="Manage Team Tags"
        />
      </div>
    }
  </section>
}

@if (identityResolved() && !currentTeamWorkspace) {
  <app-sidebar-items-divider />
  <app-knowledge-items-rail [sidebarSplitViewKey]="sidebarSplitViewKey" [tenantId]="currentTeamId" [team]="team" />
}

<ng-template #navigationItem let-item="item" let-index="index" let-depth="depth" let-team="team">
  <!--
    data-tenant: the falsy check MUST come first. On the flat personal rail both `team` and
    `currentTeamMailbox` are undefined, so an identity test alone would label every personal row 'team'.
  -->
  <app-sidebar-item
    data-testid="rail-row"
    [attr.data-filter]="item.id"
    [attr.data-tenant]="!team ? 'personal' : team === currentTeamMailbox ? 'team' : 'member'"
    class="rail-navigation-item rounded [&:not(.drag-over)]:hover:bg-hover"
    [title]="item.title"
    [icon]="item.icon"
    [depth]="depth || 0"
    [active]="(currentTeamId ?? null) === (team?.id ?? null) && sidebarFilter === item.id"
    (appClick)="selectFilter(item.id, team)"
    [appSidebarContextMenu]="item.id"
    [appSidebarContextMenuTenantId]="team?.id"
    [appSidebarContextMenuRail]="!!team && (team === currentMemberMailbox || team === currentTeamMailbox) && item.id !== 'search_results'"
    [appSidebarContextMenuRailScope]="team === currentTeamMailbox ? 'team' : 'mine'"
    #elRef
    appDroppable
    appDroppableHoverClass="drag-over"
    [appDroppablePredicate]="dndPredicate"
    (appDroppableDrop)="dndDrop($event, item.id, index, team, item.title)"
  />
</ng-template>

<!--
  The Shared header with a menu. Shared sits outside My Workspace's section, but it is the member's: its
  menu names the member workspace, never the team the rail happens to show. A right-click opens the menu
  and does not toggle the section; a click toggles it exactly as the default header does.
-->
<ng-template #notesSharedHeader let-expanded="expanded" let-toggle="toggle">
  <button
    type="button"
    class="shared-placeholder-header"
    [attr.aria-expanded]="expanded"
    (click)="toggle()"
    appSidebarContextMenu="shared"
    [appSidebarContextMenuTenantId]="currentMemberMailbox?.id"
    [appSidebarContextMenuRail]="true"
    appSidebarContextMenuRailScope="mine"
  >
    <app-icon name="sapphire-chevron-right" size="s" [class.rotate-90]="expanded" />
    Shared
  </button>
</ng-template>

<ng-template #assignedBadge>
  <span class="rail-count" aria-label="Unread assigned notes">{{ assignmentFeed?.unreadCount() }}</span>
</ng-template>

<ng-template #afterDropTemplate let-item="item">
  <app-note-quick-form
    [autofocus]="true"
    [showMore]="true"
    [title]="item.title"
    [note]="item"
    [tenantItems]="[item]"
    (more)="handleMore($event)"
    (close)="handleClose()"
    [transferredDraft]="true"
  />
</ng-template>
